// Main-thread side of the audio engine: creates the AudioContext + worklet, ships the WASM module
// and kit samples to it, sends commands (SharedArrayBuffer ring when cross-origin isolated,
// postMessage otherwise) and receives engine events (clock, hits, recorded notes, meters).
import { BASE } from "../demo/flag";
import wasmUrl from "@eb/engine-wasm/wasm-url?url";
import {
  kitFileUrl,
  kitSampleId,
  NUM_SOUNDS,
  type KitId,
  type KitManifest,
  type LoopData,
  type PeriodSpec,
} from "@eb/shared";
import type { Arrangement } from "@eb/shared";
import workletUrl from "./worklet.ts?worker&url";
import { EVENT_STRIDE, Ev, type FromWorklet, type ToWorklet } from "./protocol";
import { CMD_STRIDE, createRing, Op, Ring } from "./ring";

export interface ClockEvent {
  /** Engine sample at the start of the quantum the clock was taken in. */
  samplePos: number;
  tickPos: number;
  transportTick: number;
  patternLength: number;
  playing: boolean;
  recording: boolean;
  world: boolean;
  worldBar: number;
  worldBarStart: number;
  patternIndex: number;
  songStep: number;
  masterPeak: number;
  masterRms: number;
  activeVoices: number;
  latencySamples: number;
}

export interface EngineListener {
  clock?(c: ClockEvent): void;
  hit?(pad: number, velocity: number, note: number): void;
  release?(pad: number, note: number): void;
  recorded?(pad: number, velocity: number, tick: number, note: number): void;
  recordedRelease?(pad: number, tick: number, note: number): void;
  meters?(peaks: Float64Array): void;
}

// The engine is sent to the worklet as bytes and compiled there: Chrome (154+) no longer
// delivers a compiled WebAssembly.Module to an AudioWorklet (the message fails to deserialise).
let wasmBytes: Promise<ArrayBuffer> | null = null;
export function fetchWasm(): Promise<ArrayBuffer> {
  wasmBytes ??= fetch(wasmUrl).then(async (r) => {
    if (!r.ok) throw new Error(`engine wasm: HTTP ${r.status}`);
    return r.arrayBuffer();
  });
  return wasmBytes;
}

const kitCache = new Map<KitId, Promise<{ manifest: KitManifest; files: Map<string, ArrayBuffer> }>>();

/** Fetch a kit's manifest and WAV files (cached per page). */
export function fetchKit(kit: KitId): Promise<{ manifest: KitManifest; files: Map<string, ArrayBuffer> }> {
  let p = kitCache.get(kit);
  if (!p) {
    p = (async () => {
      const res = await fetch(BASE + kitFileUrl(kit, "kit.json").slice(1));
      if (!res.ok) throw new Error(`kit ${kit}: HTTP ${res.status}`);
      const manifest = (await res.json()) as KitManifest;
      const files = new Map<string, ArrayBuffer>();
      await Promise.all(
        manifest.pads.map(async (pad) => {
          const r = await fetch(BASE + kitFileUrl(kit, pad.file).slice(1));
          if (!r.ok) throw new Error(`kit ${kit}/${pad.file}: HTTP ${r.status}`);
          files.set(pad.file, await r.arrayBuffer());
        }),
      );
      return { manifest, files };
    })();
    kitCache.set(kit, p);
    p.catch(() => kitCache.delete(kit));
  }
  return p;
}

export class EngineHost {
  readonly ctx: AudioContext;
  readonly node: AudioWorkletNode;
  /** A tap on the engine's output (the sound the listener hears), for visualisations. */
  readonly analyser: AnalyserNode;
  readonly isolated: boolean;
  /** Context frame at engine sample 0. */
  frameOffset = 0;
  sampleRate: number;
  lastClock: ClockEvent | null = null;
  clockCount = 0;
  errors: string[] = [];
  private cmdRing: Ring | null = null;
  private evRing: Ring | null = null;
  private evRec = new Float64Array(EVENT_STRIDE);
  private listeners = new Set<EngineListener>();
  private loadedSamples = new Set<string>();
  private kitsLoaded = new Map<KitId, Promise<KitManifest>>();
  private readyPromise: Promise<void>;
  private raf = 0;

  private constructor(ctx: AudioContext, node: AudioWorkletNode, analyser: AnalyserNode, isolated: boolean) {
    this.ctx = ctx;
    this.node = node;
    this.analyser = analyser;
    this.isolated = isolated;
    this.sampleRate = ctx.sampleRate;
    let resolveReady!: () => void;
    let rejectReady!: (e: Error) => void;
    this.readyPromise = new Promise((res, rej) => ((resolveReady = res), (rejectReady = rej)));
    this.readyPromise.catch(() => undefined);
    node.port.onmessage = (e: MessageEvent<FromWorklet>) => {
      const m = e.data;
      if (m.type === "ready") {
        this.frameOffset = m.frameOffset;
        this.sampleRate = m.sampleRate;
        resolveReady();
      } else if (m.type === "error") {
        const msg = `${m.context}: ${m.message}`;
        this.errors.push(msg);
        console.warn(`[engine] ${msg}`);
        if (m.context === "init" || m.context === "messageerror")
          rejectReady(new Error("The audio engine failed to start. Please reload the page."));
      } else if (m.type === "events") {
        for (let k = 0; k < m.count; k++) this.dispatch(m.data, k * EVENT_STRIDE);
      }
    };
  }

  /** Create the context and worklet. Audio starts after `resume()` (needs a user gesture). */
  static async create(): Promise<EngineHost> {
    // iOS: play through the ringer switch like a media player.
    const nav = navigator as Navigator & { audioSession?: { type: string } };
    if (nav.audioSession) nav.audioSession.type = "playback";
    const ctx = new AudioContext({ latencyHint: "interactive" });
    sharedCtx = ctx;
    // Resume now, while the user gesture is still active: Safari (and Firefox) refuse a resume
    // that comes after an await.
    void ctx.resume().catch(() => undefined);
    await ctx.audioWorklet.addModule(workletUrl);
    const node = new AudioWorkletNode(ctx, "eb-engine", {
      numberOfInputs: 0,
      numberOfOutputs: 1,
      outputChannelCount: [2],
    });
    node.connect(ctx.destination);
    // Visualisation tap: no smoothing here (the visualiser shapes attack/release itself).
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 4096;
    analyser.smoothingTimeConstant = 0;
    node.connect(analyser);
    // `?nosab` forces the postMessage fallback (tests; browsers without cross-origin isolation).
    const forceFallback = typeof location !== "undefined" && new URLSearchParams(location.search).has("nosab");
    const isolated =
      !forceFallback && typeof SharedArrayBuffer !== "undefined" && globalThis.crossOriginIsolated === true;
    const host = new EngineHost(ctx, node, analyser, isolated);
    let rings = null;
    if (isolated) {
      const cmd = createRing(256, CMD_STRIDE);
      const events = createRing(1024, EVENT_STRIDE);
      host.cmdRing = new Ring(cmd);
      host.evRing = new Ring(events);
      rings = { cmd, events };
    }
    const wasm = (await fetchWasm()).slice(0);
    host.send({ type: "init", wasm, rings }, [wasm]);
    host.startPolling();
    return host;
  }

  async resume(): Promise<void> {
    if (this.ctx.state !== "running") {
      // A resume the browser won't allow stays pending forever: give up visibly instead.
      await Promise.race([this.ctx.resume(), new Promise((r) => setTimeout(r, 3000))]);
      if ((this.ctx.state as AudioContextState) !== "running")
        throw new Error("The browser blocked audio. Press the button again to start it.");
    }
    await this.readyPromise;
    this.send({ type: "latency", samples: this.outputLatencySeconds() * this.sampleRate });
  }

  get ready(): Promise<void> {
    return this.readyPromise;
  }

  /** Output latency (device + base) in seconds; 0 where the browser doesn't report it. */
  outputLatencySeconds(): number {
    const c = this.ctx as AudioContext & { outputLatency?: number };
    return (c.outputLatency ?? 0) + (c.baseLatency ?? 0);
  }

  /**
   * Engine sample that is audible right now, and the matching `performance.now()` time. Uses
   * `getOutputTimestamp()` (what the speakers play at `performanceTime`).
   */
  audibleNow(): { sample: number; perfMs: number } {
    const ts = this.ctx.getOutputTimestamp?.();
    if (ts && ts.contextTime !== undefined && ts.performanceTime !== undefined && ts.performanceTime > 0) {
      return { sample: ts.contextTime * this.sampleRate - this.frameOffset, perfMs: ts.performanceTime };
    }
    const lat = this.outputLatencySeconds();
    return { sample: (this.ctx.currentTime - lat) * this.sampleRate - this.frameOffset, perfMs: performance.now() };
  }

  addListener(l: EngineListener): () => void {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  }

  send(m: ToWorklet, transfer: Transferable[] = []): void {
    this.node.port.postMessage(m, transfer);
  }

  private command(op: Op, a = 0, b = 0, c = 0): void {
    const rec = [op, a, b, c];
    if (this.cmdRing?.push(rec)) return;
    this.send({ type: "cmd", rec });
  }

  /** Load a kit's samples into the worklet (once per kit). Returns the manifest. */
  loadKit(kit: KitId): Promise<KitManifest> {
    let p = this.kitsLoaded.get(kit);
    if (!p) {
      p = fetchKit(kit).then(({ manifest, files }) => {
        for (const pad of manifest.pads) {
          const id = kitSampleId(kit, pad.file);
          if (this.loadedSamples.has(id)) continue;
          const bytes = files.get(pad.file)!.slice(0);
          this.send({ type: "sample", id, bytes }, [bytes]);
          this.loadedSamples.add(id);
        }
        return manifest;
      });
      this.kitsLoaded.set(kit, p);
      p.catch(() => this.kitsLoaded.delete(kit));
    }
    return p;
  }

  setEditor(period: PeriodSpec, loop: LoopData, arrangement: Arrangement, neighbour?: LoopData): void {
    this.send({
      type: "editor",
      period: JSON.stringify(period),
      loop: JSON.stringify(loop),
      arrangement: JSON.stringify(arrangement),
      neighbour: neighbour ? JSON.stringify(neighbour) : "",
    });
  }

  play(): void {
    this.send({ type: "transport", action: "play" });
  }
  stop(): void {
    this.send({ type: "transport", action: "stop" });
  }
  record(): void {
    this.send({ type: "transport", action: "record" });
  }
  setRecording(on: boolean): void {
    this.send({ type: "recording", on });
  }
  noteOn(pad: number, velocity: number, note = -1): void {
    this.command(Op.NoteOn, pad, velocity, note);
  }
  noteOff(pad: number, note = -1): void {
    this.command(Op.NoteOff, pad, note);
  }
  noteRepeat(enabled: boolean, rateTicks: number): void {
    this.command(Op.NoteRepeat, enabled ? 1 : 0, rateTicks);
  }
  allNotesOff(): void {
    this.command(Op.AllNotesOff);
  }

  setPeriod(spec: PeriodSpec): void {
    this.send({ type: "period", json: JSON.stringify(spec) });
  }
  setLoop(day: number, slot: number, loop: LoopData, gain: number): void {
    this.send({ type: "loop", day, slot, json: JSON.stringify(loop), gain });
  }
  /** World mode at the stream's tempo `bpm`: world `bar` starts at engine sample `sample`. */
  locate(bar: number, sample: number, bpm: number): void {
    this.send({ type: "locate", bar, sample, bpm });
  }
  nudge(samples: number): void {
    this.send({ type: "nudge", samples });
  }
  worldStop(): void {
    this.send({ type: "world-stop" });
  }

  private startPolling() {
    const tick = () => {
      if (this.evRing) {
        while (this.evRing.pop(this.evRec)) this.dispatch(this.evRec, 0);
      }
      this.raf = requestAnimationFrame(tick);
    };
    this.raf = requestAnimationFrame(tick);
  }

  private dispatch(d: ArrayLike<number>, o: number) {
    switch (d[o]) {
      case Ev.Clock: {
        const flags = d[o + 5];
        const c: ClockEvent = {
          samplePos: d[o + 1],
          tickPos: d[o + 2],
          transportTick: d[o + 3],
          patternLength: d[o + 4],
          playing: (flags & 1) !== 0,
          recording: (flags & 2) !== 0,
          world: (flags & 4) !== 0,
          worldBar: d[o + 6],
          worldBarStart: d[o + 7],
          patternIndex: d[o + 8],
          songStep: d[o + 9],
          masterPeak: d[o + 10],
          masterRms: d[o + 11],
          activeVoices: d[o + 12],
          latencySamples: d[o + 13],
        };
        this.lastClock = c;
        this.clockCount++;
        for (const l of this.listeners) l.clock?.(c);
        break;
      }
      case Ev.PadHit:
        for (const l of this.listeners) l.hit?.(d[o + 1], d[o + 2], d[o + 3]);
        break;
      case Ev.PadRelease:
        for (const l of this.listeners) l.release?.(d[o + 1], d[o + 2]);
        break;
      case Ev.Recorded:
        for (const l of this.listeners) l.recorded?.(d[o + 1], d[o + 2], d[o + 3], d[o + 4]);
        break;
      case Ev.RecordedRelease:
        for (const l of this.listeners) l.recordedRelease?.(d[o + 1], d[o + 2], d[o + 3]);
        break;
      case Ev.Meters: {
        const peaks = new Float64Array(NUM_SOUNDS);
        for (let i = 0; i < NUM_SOUNDS; i++) peaks[i] = d[o + 1 + i];
        for (const l of this.listeners) l.meters?.(peaks);
        break;
      }
    }
  }

  async close(): Promise<void> {
    cancelAnimationFrame(this.raf);
    await this.ctx.close();
  }
}

let shared: Promise<EngineHost> | null = null;

/** The page-wide engine host (created on first use). */
let sharedCtx: AudioContext | null = null;

let created: EngineHost | null = null;

/** The engine if it has been created already (never creates one). */
export function currentHost(): EngineHost | null {
  return created;
}

/** Get the shared engine. Call it synchronously from a user gesture so audio can start. */
export function engineHost(): Promise<EngineHost> {
  if (sharedCtx && sharedCtx.state !== "running") void sharedCtx.resume().catch(() => undefined);
  shared ??= EngineHost.create().then((h) => {
    (globalThis as unknown as { __ebHost?: EngineHost }).__ebHost = h;
    created = h;
    return h;
  });
  shared.catch(() => (shared = null));
  return shared;
}
